import { useEffect, useState } from 'react';

import { adminApiFetch } from '../../lib';

import type { WebsitePageType } from '@utazzo/shared';

/**
 * Server's answer for one slug. `suggestion` is a free slug near the one asked
 * for - for the collision that actually happens (the Best Places and the Best
 * Time guide for the same destination both wanting `/bali`) it is the qualified
 * form that guide would have carried anyway.
 */
interface SlugCheckResponse {
  slug: string;
  available: boolean;
  reason?: string;
  suggestion?: string;
}

export interface SlugAvailability {
  status: 'idle' | 'checking' | 'free' | 'taken';
  /** Why it is taken, from the server. */
  reason?: string;
  suggestion?: string;
}

const IDLE: SlugAvailability = { status: 'idle' };

/**
 * Is this slug free, checked while the author types?
 *
 * Two guide types now share the root namespace, so `/bali` can only belong to
 * one page. Without this the author learns that from a failed save, after the
 * fact and with the field still showing a slug the page doesn't have. Asking
 * the server as they type turns it into a hint beside the input, with a URL
 * they can take in one click.
 *
 * Pass `slug: null` when there is nothing to check (unchanged, empty, or
 * already invalid) - the hook then stays idle and makes no request.
 *
 * The check is advisory. The save path runs the same rule server-side, so a
 * slug claimed by someone else in the meantime is still refused there.
 */
export function useSlugAvailability(
  pageType: WebsitePageType,
  slug: string | null,
  excludePageId?: string,
): SlugAvailability {
  const [state, setState] = useState<SlugAvailability>(IDLE);

  useEffect(() => {
    if (!slug) {
      setState(IDLE);
      return;
    }

    setState({ status: 'checking' });
    const controller = new AbortController();
    // Debounced: one request per pause in typing, not one per keystroke.
    const timer = setTimeout(() => {
      const query = new URLSearchParams({ pageType, slug });
      if (excludePageId) query.set('excludePageId', excludePageId);

      adminApiFetch<SlugCheckResponse>(`/admin/website/pages/slug-check?${query}`, {
        signal: controller.signal,
      })
        .then((res) => {
          if (controller.signal.aborted) return;
          setState(
            res.available
              ? { status: 'free' }
              : { status: 'taken', reason: res.reason, suggestion: res.suggestion },
          );
        })
        .catch(() => {
          // A failed check must not block the author: fall back to idle and let
          // the save path have the final say.
          if (!controller.signal.aborted) setState(IDLE);
        });
    }, 400);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [pageType, slug, excludePageId]);

  return state;
}
